/**
 * Obras — Página de gestión de obras dentro del módulo Ingeniería.
 * Una obra pertenece a un cliente y puede tener varios presupuestos (principal
 * + adicionales). CRUD contra /api/listas/obras (el código OB-... lo genera el
 * backend). La columna PRESUPUESTOS cuenta los budgets vinculados (obra_id) más
 * el match legacy por nombre de obra.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Building2, Plus, Search, Pencil, Trash2, Loader2, Copy, Check, FileText,
} from "lucide-react";
import { API } from "../../contexts/AuthContext";
import { toast } from "sonner";
import ObraFormModal from "../../components/ObraFormModal";

function CodeBadge({ code }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_e) { /* noop */ }
  };
  return (
    <button onClick={copy}
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md font-mono text-[11px] font-bold tracking-wide transition-all hover:brightness-110"
      style={{ background: "#8B5CF615", color: "#7C3AED", border: "1px solid #8B5CF630" }}
      title="Click para copiar">
      {code}
      {copied ? <Check size={10} /> : <Copy size={10} className="opacity-50" />}
    </button>
  );
}

export default function Obras() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterCliente, setFilterCliente] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [oRes, cRes, bRes] = await Promise.all([
        axios.get(`${API}/listas/obras`),
        axios.get(`${API}/listas/clientes`).catch(() => ({ data: [] })),
        axios.get(`${API}/budgets`).catch(() => ({ data: [] })),
      ]);
      setItems(oRes.data || []);
      setClientes(cRes.data || []);
      setBudgets(Array.isArray(bRes.data) ? bRes.data : []);
    } catch (_e) {
      toast.error("Error al cargar obras");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSaved = async () => {
    setOpen(false);
    setEditing(null);
    await load();
  };

  const handleDelete = async (x) => {
    const n = countPresupuestos(x);
    const msg = n > 0
      ? `Si esta obra tiene presupuestos asociados, esos presupuestos quedarán sin obra. ¿Continuar? (${n} presupuesto(s) vinculado(s))`
      : "¿Seguro que querés eliminar esta obra?";
    if (!window.confirm(msg)) return;
    try {
      await axios.delete(`${API}/listas/obras/${x._id}`);
      toast.success("Obra eliminada");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo eliminar la obra");
    }
  };

  const countPresupuestos = (obra) => {
    const obraName = (obra.obra || "").toLowerCase();
    return budgets.filter((b) =>
      (b.obra_id && b.obra_id === obra._id) ||
      (obraName && (b.obra || "").toLowerCase() === obraName)
    ).length;
  };

  const filtered = items.filter((x) => {
    const q = search.toLowerCase();
    const matchSearch = !q || [x.obra, x.codigo, x.cliente_nombre, x.cliente_codigo, x.direccion]
      .some((v) => (v || "").toLowerCase().includes(q));
    const matchCliente = !filterCliente || x.cliente_id === filterCliente;
    return matchSearch && matchCliente;
  });

  return (
    <div className="space-y-4 p-4 md:p-6" data-testid="obras-page">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight flex items-center gap-2">
            <Building2 size={22} className="text-[#7C3AED]" /> Obras
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Gestión de obras — vinculadas a un cliente. Una obra puede tener varios presupuestos (principal + adicionales).
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={filterCliente} onChange={(e) => setFilterCliente(e.target.value)}
            className="px-3 py-2 text-[13px] rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-gray-400 focus:outline-none"
            data-testid="obras-filter-cliente">
            <option value="">Todos los clientes</option>
            {clientes.map((c) => <option key={c._id} value={c._id}>{`${c.codigo} — ${c.cliente}`}</option>)}
          </select>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por obra, código, cliente..."
              className="pl-9 pr-3 py-2 text-[13px] rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-gray-400 focus:outline-none w-60"
              data-testid="obras-search"
            />
          </div>
          <button
            onClick={() => { setEditing(null); setOpen(true); }}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-[13px] font-semibold text-white shadow-sm hover:brightness-110 transition-all bg-[#7C3AED]"
            data-testid="obra-new-btn"
          >
            <Plus size={14} /> <span>Nueva Obra</span>
          </button>
        </div>
      </div>

      {/* Table / states */}
      {loading ? (
        <div className="bg-white rounded-2xl p-8 text-center text-gray-400">
          <Loader2 size={20} className="mx-auto animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-12 text-center">
          <div className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center mb-3 bg-[#8B5CF615] text-[#7C3AED]">
            <Building2 size={22} />
          </div>
          <h4 className="text-[14px] font-semibold text-gray-800">
            {search || filterCliente ? "Sin resultados para tu búsqueda" : "Aún no hay obras"}
          </h4>
          {!search && !filterCliente && (
            <button onClick={() => { setEditing(null); setOpen(true); }}
              className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-[13px] font-semibold text-white bg-[#7C3AED]">
              <Plus size={14} /> Crear Obra
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="bg-gray-50">
                <tr className="text-left text-[11px] uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3 font-semibold">Código</th>
                  <th className="px-4 py-3 font-semibold">Obra</th>
                  <th className="px-4 py-3 font-semibold">Cliente</th>
                  <th className="px-4 py-3 font-semibold">Dirección</th>
                  <th className="px-4 py-3 font-semibold text-center">Presupuestos</th>
                  <th className="px-4 py-3 font-semibold text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((x) => {
                  const nPresup = countPresupuestos(x);
                  return (
                    <tr key={x._id} className="border-t border-gray-100 hover:bg-gray-50/60" data-testid={`obra-row-${x.codigo}`}>
                      <td className="px-4 py-2.5"><CodeBadge code={x.codigo} /></td>
                      <td className="px-4 py-2.5 font-semibold text-gray-900">{x.obra}</td>
                      <td className="px-4 py-2.5">
                        {x.cliente_nombre ? (
                          <span className="inline-flex items-center gap-1.5">
                            <span className="font-mono text-[11px] font-bold text-[#2E6DA4]">{x.cliente_codigo}</span>
                            <span className="text-gray-700">{x.cliente_nombre}</span>
                          </span>
                        ) : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-2.5 text-gray-600 truncate max-w-[200px]">{x.direccion || "—"}</td>
                      <td className="px-4 py-2.5 text-center">
                        <button
                          onClick={() => navigate(`/ingenieria/presupuestos?obra_id=${x._id}`)}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[12px] font-semibold bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors"
                          title="Ver presupuestos de esta obra"
                          data-testid={`obra-ver-presupuestos-${x.codigo}`}>
                          <FileText size={12} /> {nPresup}
                        </button>
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <div className="inline-flex items-center gap-1">
                          <button onClick={() => { setEditing(x); setOpen(true); }}
                            className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-600" title="Editar"
                            data-testid={`obra-edit-${x.codigo}`}>
                            <Pencil size={13} />
                          </button>
                          <button onClick={() => handleDelete(x)}
                            className="p-1.5 rounded-lg hover:bg-red-50 text-red-600" title="Eliminar"
                            data-testid={`obra-delete-${x.codigo}`}>
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {open && (
        <ObraFormModal
          initial={editing}
          clientes={clientes}
          onClose={() => { setOpen(false); setEditing(null); }}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
